import { Instagram, Facebook } from "lucide-react";
import Logo from "./Logo";
import { scrollToSection } from "./Navbar";
import { useLang } from "../context/LanguageContext";
import useSettings from "../hooks/useSettings";

export default function Footer() {
  const { lang } = useLang();
  const settings = useSettings();
  const socials = [
    { url: settings.instagram_url, Icon: Instagram, label: "Instagram", testid: "footer-instagram-link" },
    { url: settings.facebook_url, Icon: Facebook, label: "Facebook", testid: "footer-facebook-link" },
  ].filter((s) => s.url);
  const links = [
    ["storia", { it: "La Storia", en: "Our Story" }],
    ["menu", { it: "Il Menù", en: "The Menu" }],
    ["lombate", { it: "Vetrina Lombate", en: "Steak Display" }],
    ["galleria", { it: "Galleria", en: "Gallery" }],
    ["contatti", { it: "Prenota un Tavolo", en: "Book a Table" }],
  ];

  return (
    <footer data-testid="site-footer" className="relative border-t border-[rgba(201,162,101,0.18)] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-10 relative">
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center">
              <Logo className="h-16 w-16" />
            </div>
            <p className="mt-6 text-sm text-[#7A7267] leading-relaxed max-w-xs">
              {lang === "it"
                ? "Cucina piemontese, proposte alternative e vetrina delle carni nel cuore di Casale Monferrato. Riaperto dopo più di trentacinque anni, con la stessa anima di sempre."
                : "Piedmontese cuisine, creative dishes and a meat display in the heart of Casale Monferrato. Reopened after more than thirty-five years, with the same soul as ever."}
            </p>
            {socials.length > 0 && (
              <div className="mt-6">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#7A7267] font-cinzel">
                  {lang === "it" ? "Seguici" : "Follow us"}
                </p>
                <div className="mt-3 flex gap-3">
                  {socials.map(({ url, Icon, label, testid }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={testid}
                      aria-label={label}
                      className="h-11 w-11 rounded-full border border-[rgba(201,162,101,0.4)] flex items-center justify-center text-[#C9A265] hover:bg-[#C9A265] hover:text-[#0D0C0A] transition-colors duration-300"
                    >
                      <Icon size={18} />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A265] font-cinzel">
              {lang === "it" ? "Esplora" : "Explore"}
            </p>
            <div className="mt-5 flex flex-col gap-3">
              {links.map(([id, label]) => (
                <button
                  key={id}
                  data-testid={`footer-link-${id}`}
                  onClick={() => scrollToSection(id)}
                  className="text-left text-sm text-[#B0A89C] hover:text-[#C9A265] transition-colors w-fit"
                >
                  {label[lang]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A265] font-cinzel">
              {lang === "it" ? "Dove Trovarci" : "Find Us"}
            </p>
            <p className="mt-5 text-sm text-[#B0A89C] leading-relaxed">
              Piazza Castello 41<br />
              15033 Casale Monferrato (AL)<br />
              <a href="tel:+393514369996" className="hover:text-[#C9A265] transition-colors">
                +39 351 436 9996
              </a>
            </p>
            <a
              data-testid="footer-directions-link"
              href="https://www.google.com/maps/search/?api=1&query=Piazza+Castello+41+Casale+Monferrato"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-block px-6 py-3 border border-[rgba(201,162,101,0.4)] text-[#C9A265] text-[10px] uppercase tracking-[0.25em] font-cinzel rounded-full hover:bg-[#C9A265] hover:text-[#0D0C0A] transition-colors duration-300"
            >
              {lang === "it" ? "Indicazioni Stradali" : "Get Directions"}
            </a>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-[rgba(255,255,255,0.06)] flex flex-wrap justify-between items-center gap-4">
          <p className="text-xs text-[#7A7267]">© 2026 Ristorante Milano — Casale Monferrato. {lang === "it" ? "Tutti i diritti riservati." : "All rights reserved."}</p>
          <div className="flex items-center gap-6">
            <p className="text-xs text-[#7A7267] font-cinzel tracking-[0.2em] uppercase">
              {lang === "it" ? "Dal passato, al futuro della tavola" : "From the past, to the future of the table"}
            </p>
            <a href="/admin" data-testid="footer-admin-link" className="text-xs text-[#7A7267] hover:text-[#C9A265] font-cinzel tracking-[0.2em] uppercase transition-colors">
              {lang === "it" ? "Area Riservata" : "Staff Area"}
            </a>
          </div>
        </div>
      </div>
      <div
        aria-hidden
        className="pointer-events-none select-none absolute -bottom-10 left-1/2 -translate-x-1/2 font-cinzel text-[22vw] leading-none tracking-[0.1em] text-[#C9A265] opacity-[0.04] whitespace-nowrap"
      >
        MILANO
      </div>
    </footer>
  );
}
